import { useState, useEffect, useRef } from 'react';
import {
  ChevronDown, ChevronUp, Shield, Crown,
  AlertTriangle, Megaphone, CheckCheck, Send,
  Search, X, ArrowLeft, Inbox, MessageSquare,
  Image, Mic, Square, Clock, CheckCircle
} from 'lucide-react';
import { useVisibleStore } from '../hooks/useVisibleStore';
import { useStore } from '../store/useStore';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { useMessages } from '../hooks/useMessages';

// Sender labels encode message type — read by team app for styling
export const BROADCAST_LABELS = {
  alert: '🚨 Alerte Danger',
  note:  '📢 Note Générale',
};

export function MessagesView() {
  const {
    currentUser,
    categories,
    addMessage,
    addBroadcastMessage,
    markAllMessagesRead,
    markTeamMessagesRead,
    team: teamList,
    approvalRequests,
    approveRequest,
    rejectRequest,
  } = useVisibleStore();

  // ── Shared message logic (also used by MessagingDrawer) ──
  const {
    myTeamIds, rawMessages,
    messagesByTeam, sortedTeam,
    unreadCountForTeam, totalUnread,
    getContactPreview, getActiveMessages,
    getSenderLabel, isEmailValid,
    isAdmin, isManager,
  } = useMessages();

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(() => {
    // Auto-select team from URL param
    const params = new URLSearchParams(window.location.search);
    return params.get('team_id') || null;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isBroadcastCollapsed, setIsBroadcastCollapsed] = useState(true);
  const [content, setContent] = useState('');
  const [attachedImage, setAttachedImage] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Broadcast states
  const [broadcastType, setBroadcastType] = useState<'alert' | 'note'>('alert');
  const [broadcastCategoryId, setBroadcastCategoryId] = useState<string>('all');
  const [broadcastContent, setBroadcastContent] = useState('');
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastSent, setBroadcastSent] = useState(false);
  const [isApprovalCollapsed, setIsApprovalCollapsed] = useState(false);

  // Auto-scroll chat history ref
  const chatEndRef = useRef<HTMLDivElement>(null);

  const chatScrollRef = useRef<HTMLDivElement>(null);

  // ── Supabase Realtime + Polling for new messages ──
  useEffect(() => {
    if (!isAdmin && !isManager) return;

    const visibleIds = new Set(teamList.map(s => s.id));
    if (visibleIds.size === 0) return;

    let mounted = true;
    const mapMsg = (row: any) => ({
      id: row.id, teamId: row.staff_id, orgId: row.org_id, projectId: row.project_id,
      content: row.content, timestamp: row.created_at, read: row.read ?? false,
      fromTeam: row.from_staff ?? false, senderName: row.sender_name ?? undefined,
      recipientTeamId: row.recipient_staff_id ?? undefined,
      imageUrl: row.image_url ?? null, voiceUrl: row.voice_url ?? null,
    });

    // Realtime subscription: instant message delivery
    const channel = supabase
      .channel('messages-view-realtime')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
      }, (payload: any) => {
        if (!mounted) return;
        const row = payload.new;
        // Only accept messages from visible team members or addressed to me
        if (visibleIds.has(row.staff_id) || myTeamIds.has(row.recipient_staff_id || '')) {
          useStore.setState((s) => {
            if ((s.messages || []).some(m => m.id === row.id)) return s; // dedup
            return { messages: [...(s.messages || []), mapMsg(row)] };
          });
        }
      })
      .subscribe();

    // Polling backup (30s — réduit de 5s pour économiser l'egress)
    let lastCheck = new Date().toISOString();
    const MSG_COLUMNS = 'id, staff_id, org_id, project_id, content, created_at, read, from_staff, sender_name, recipient_staff_id';
    const poll = async () => {
      if (!mounted || document.hidden) return;
      try {
        const ids = Array.from(visibleIds);
        for (let i = 0; i < ids.length; i += 50) {
          const batch = ids.slice(i, i + 50);
          const { data: newMsgs } = await supabase
            .from('messages').select(MSG_COLUMNS).in('staff_id', batch)
            .gt('created_at', lastCheck).is('deleted_at', null)
            .order('created_at', { ascending: true });

          if (newMsgs && newMsgs.length > 0 && mounted) {
            if (newMsgs[newMsgs.length - 1].created_at > lastCheck) lastCheck = newMsgs[newMsgs.length - 1].created_at;
            useStore.setState((s) => {
              const existingIds = new Set((s.messages || []).map(m => m.id));
              const fresh = newMsgs.map(mapMsg).filter((m: any) => !existingIds.has(m.id));
              return fresh.length > 0 ? { messages: [...(s.messages || []), ...fresh] } : s;
            });
          }
        }
      } catch (_) { /* ignore */ }
    };

    const interval = setInterval(poll, 30000);
    return () => { mounted = false; clearInterval(interval); supabase.removeChannel(channel); };
  }, [isAdmin, isManager, teamList.length, myTeamIds.size]);

  const getBroadcastTargets = (): string[] => {
    let targets = teamList;
    if (isManager && currentUser.categoryId) {
      targets = targets.filter(s => s.categoryId === currentUser.categoryId);
    } else if (broadcastCategoryId !== 'all') {
      targets = targets.filter(s => s.categoryId === broadcastCategoryId);
    }
    return targets.map(s => s.id);
  };

  const handleBroadcast = () => {
    if (!broadcastContent.trim()) return;
    const teamIds = getBroadcastTargets();
    if (teamIds.length === 0) return;
    setBroadcastSending(true);
    addBroadcastMessage(teamIds, broadcastContent.trim(), BROADCAST_LABELS[broadcastType as keyof typeof BROADCAST_LABELS]);
    setTimeout(() => {
      setBroadcastSending(false);
      setBroadcastSent(true);
      setBroadcastContent('');
      setTimeout(() => setBroadcastSent(false), 3000);
    }, 500);
  };

  const broadcastCount = getBroadcastTargets().length;

  // Approval requests visible for this admin/manager
  const visibleApprovalRequests = (approvalRequests || []).filter(r => {
    if (r.status !== 'pending') return false;
    if (isAdmin) return true;
    if (isManager && currentUser.categoryId) {
      return r.categoryId === currentUser.categoryId;
    }
    return false;
  });
  const approvalCount = visibleApprovalRequests.length;

  // Expiry timer component
  function ExpiryTimer({ expiresAt }: { expiresAt: string }) {
    const [remaining, setRemaining] = useState('');
    useEffect(() => {
      const update = () => {
        const diff = new Date(expiresAt).getTime() - Date.now();
        if (diff <= 0) setRemaining('Expiré');
        else {
          const h = Math.floor(diff / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          setRemaining(`${h}h ${m < 10 ? '0' : ''}${m}m`);
        }
      };
      update();
      const id = setInterval(update, 30000);
      return () => clearInterval(id);
    }, [expiresAt]);
    return (
      <span className={`text-[9px] font-mono font-bold ${remaining === 'Expiré' ? 'text-red-400' : 'text-amber-400'}`}>
        <Clock className="w-2.5 h-2.5 inline mr-0.5" />
        {remaining}
      </span>
    );
  }

  const resolveSender = (m: { fromTeam?: boolean; senderName?: string }, teamName: string) => {
    if (m.senderName) return m.senderName;
    return m.fromTeam ? teamName : 'Admin / Manager';
  };

  // Group messages by team. Use raw (unfiltered) messages to avoid cross-org blindness.
  // Auto-mark read when thread opens or new messages arrive
  useEffect(() => {
    if (selectedTeamId) {
      const unreadCount = unreadCountForTeam(selectedTeamId);
      if (unreadCount > 0) {
        markTeamMessagesRead(selectedTeamId);
      }
      // Also mark admin->me messages (fromTeam=false) on my own team records
      for (const myTid of myTeamIds) {
        const unreadOnMyTeam = (messagesByTeam[myTid] || []).filter(m => !m.read && !m.fromTeam);
        unreadOnMyTeam.forEach(m => { useStore.getState().markMessageRead(m.id); });
      }
    }
  }, [selectedTeamId, rawMessages, markTeamMessagesRead, myTeamIds]);

  // Auto-select contact with most recent unread message (when no team selected)
  useEffect(() => {
    if (selectedTeamId) return; // already selected
    // Find the contact with the most recent unread message addressed to me
    let bestTeamId: string | null = null;
    let bestTime = 0;
    for (const teamId of Object.keys(messagesByTeam)) {
      const msgs = messagesByTeam[teamId];
      const unreadForMe = msgs.filter(m =>
        !m.read && m.fromTeam && (!m.recipientTeamId || myTeamIds.has(m.recipientTeamId))
      );
      if (unreadForMe.length > 0) {
        const latestTime = Math.max(...unreadForMe.map(m => new Date(m.timestamp).getTime()));
        if (latestTime > bestTime) {
          bestTime = latestTime;
          bestTeamId = teamId;
        }
      }
    }
    if (bestTeamId) {
      setSelectedTeamId(bestTeamId);
    }
  }, [rawMessages, selectedTeamId, messagesByTeam, myTeamIds]);

  // Scroll to bottom on new message
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTeamId, rawMessages]);

  const SenderBadge = ({ name, isFromTeam }: { name: string; isFromTeam?: boolean }) => {
    const isAdminSender = name === 'Admin';
    const isManager = name?.startsWith('Manager');
    const isAlert = name?.includes('Alerte');
    const isNote = name?.includes('Note');
    return (
      <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider ${
        isFromTeam ? 'text-emerald-400'
        : isAlert ? 'text-red-400 font-black'
        : isNote ? 'text-sky-400'
        : isAdminSender ? 'text-amber-400'
        : isManager ? 'text-indigo-400'
        : 'text-indigo-400'
      }`}>
        {isAlert && <AlertTriangle className="w-2.5 h-2.5" />}
        {isNote && <Megaphone className="w-2.5 h-2.5" />}
        {isAdminSender && !isAlert && !isNote && <Shield className="w-2.5 h-2.5" />}
        {isManager && !isAlert && !isNote && <Crown className="w-2.5 h-2.5" />}
        {name}
      </span>
    );
  };

  const handleSendMessage = () => {
    if (!selectedTeamId) return;
    const hasContent = content.trim();
    const hasImage = !!attachedImage;
    const hasVoice = !!audioBlob;
    if (!hasContent && !hasImage && !hasVoice) return;

    if (hasVoice && audioBlob) {
      const reader = new FileReader();
      reader.onloadend = () => {
        addMessage(selectedTeamId, content.trim() || '🎤 Message vocal', getSenderLabel(), null, reader.result as string);
        setContent(''); setAudioBlob(null);
      };
      reader.readAsDataURL(audioBlob);
    } else if (hasImage) {
      addMessage(selectedTeamId, content.trim() || '📷 Image', getSenderLabel(), attachedImage, null);
      setContent(''); setAttachedImage(null);
    } else {
      addMessage(selectedTeamId, content.trim(), getSenderLabel(), null, null);
      setContent('');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = recorder;
      const chunks: Blob[] = [];
      recorder.ondataavailable = e => chunks.push(e.data);
      recorder.onstop = () => {
        setAudioBlob(new Blob(chunks, { type: 'audio/webm' }));
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      setRecording(true);
    } catch (err) {
      console.error('Microphone error:', err);
      alert('Impossible d\'accéder au microphone.');
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleImagePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const compressed = await compressImage(file, 400, 400, 0.5);
    setAttachedImage(compressed);
  };

  const activeTeam = selectedTeamId ? teamList.find(s => s.id === selectedTeamId) : null;
  const activeMessages = selectedTeamId && activeTeam
    ? getActiveMessages(selectedTeamId, activeTeam.name)
    : [];

  // Auto-scroll chat to bottom when messages or conversation change
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [activeMessages, selectedTeamId]);

  return (
    <div className="h-full flex flex-col md:flex-row overflow-hidden bg-[#0A0B0E] font-sans relative">
      {/* ─── LEFT COLUMN: CONTACTS LIST & BROADCAST ─── */}
      <div className="w-full md:w-[350px] lg:w-[380px] h-full flex flex-col border-r border-[#2D3139] bg-[#14161B]/30 shrink-0 overflow-hidden">
        {/* Header */}
        <div className="p-5 border-b border-[#2D3139] flex flex-col gap-3 bg-[#1A1D23]/60">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="relative">
                <MessageSquare className="w-5 h-5 text-indigo-400" />
                {totalUnread > 0 && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 rounded-full animate-orange-blink border border-[#1A1D23]" />
                )}
              </div>
              <h2 className="text-lg font-bold text-white tracking-wide">Communication</h2>
            </div>
            {totalUnread > 0 && (
              <button
                onClick={markAllMessagesRead}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20 rounded-xl font-bold transition-all text-xs"
              >
                <CheckCheck className="w-3.5 h-3.5" />
                Tout lu
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs text-slate-400 leading-normal">
              {totalUnread > 0 ? `${totalUnread} message(s) non lu(s)` : 'Tous les messages sont lus'}
            </p>
            {approvalCount > 0 && (
              <span className="text-[9px] bg-amber-500/10 text-amber-400 border border-amber-500/25 px-1.5 py-0.5 rounded-full font-extrabold">
                {approvalCount} à valider
              </span>
            )}
          </div>
        </div>

        {/* Scrollable list & filters */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
          {/* Demandes en attente de validation */}
          {approvalCount > 0 && (
            <div className="bg-[#0A0B0E]/60 border border-amber-500/20 rounded-2xl p-4 space-y-3">
              <button
                onClick={() => setIsApprovalCollapsed(!isApprovalCollapsed)}
                className="w-full flex items-center gap-2 text-left focus:outline-none"
              >
                <Clock className="w-4 h-4 text-amber-400" />
                <h3 className="font-bold text-white text-xs">Demandes en attente</h3>
                <span className="text-[9px] bg-amber-500/10 text-amber-400 border border-amber-500/25 px-2 py-0.5 rounded-full font-extrabold ml-1.5">
                  {approvalCount}
                </span>
                <span className="ml-auto text-slate-400">
                  {isApprovalCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                </span>
              </button>

              {!isApprovalCollapsed && (
                <div className="space-y-2 pt-2 border-t border-[#2D3139]/30">
                  {visibleApprovalRequests.map(req => (
                    <div key={req.id} className="bg-[#14161B] rounded-xl p-3 border border-[#2D3139] space-y-2">
                      {/* Header: team name, role, time remaining */}
                      <div className="flex justify-between items-start">
                        <div>
                          <span className="font-bold text-white text-xs">{req.teamName}</span>
                          <span className="text-[9px] text-slate-500 ml-1.5">
                            → {req.recipientNames.length} destinataire{req.recipientNames.length > 1 ? 's' : ''}
                          </span>
                        </div>
                        <ExpiryTimer expiresAt={req.expiresAt} />
                      </div>

                      {/* Content preview */}
                      <p className="text-[11px] text-slate-300 leading-relaxed line-clamp-2">{req.content}</p>

                      {/* Recipient names */}
                      <p className="text-[9px] text-indigo-400/70 leading-relaxed truncate">
                        À: {req.recipientNames.join(', ')}
                      </p>

                      {/* Image preview */}
                      {req.imageUrl && (
                        <img src={req.imageUrl} className="max-w-[120px] max-h-[80px] rounded-lg border border-[#2D3139] object-cover" alt="preview" />
                      )}
                      {/* Voice preview */}
                      {req.voiceUrl && (
                        <audio src={req.voiceUrl} controls className="h-7 w-full max-w-[200px]" preload="metadata" />
                      )}

                      {/* Action buttons */}
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => approveRequest(req.id, currentUser.email || 'Admin')}
                          className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-[10px] rounded-xl transition-colors flex items-center justify-center gap-1.5"
                        >
                          <CheckCircle className="w-3 h-3" /> Approuver
                        </button>
                        <button
                          onClick={() => rejectRequest(req.id)}
                          className="flex-1 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 font-bold text-[10px] rounded-xl transition-colors flex items-center justify-center gap-1.5"
                        >
                          <X className="w-3 h-3" /> Rejeter
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Message Collectif (Broadcast) */}
          <div className="bg-[#0A0B0E]/60 border border-red-500/20 rounded-2xl p-4 space-y-3">
            <button 
              onClick={() => setIsBroadcastCollapsed(!isBroadcastCollapsed)}
              className="w-full flex items-center gap-2 text-left focus:outline-none"
            >
              <Megaphone className="w-4 h-4 text-red-400 animate-pulse" />
              <h3 className="font-bold text-white text-xs">Message Collectif</h3>
              <span className="text-[9px] bg-red-500/10 text-red-400 border border-red-500/25 px-2 py-0.5 rounded-full font-extrabold ml-1.5">
                {broadcastCount} cibles
              </span>
              <span className="ml-auto text-slate-400">
                {isBroadcastCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
              </span>
            </button>

            {!isBroadcastCollapsed && (
              <div className="space-y-3 pt-2 border-t border-[#2D3139]/30">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setBroadcastType('alert')}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[10px] font-bold transition-all ${
                      broadcastType === 'alert'
                        ? 'bg-red-500/20 border-red-500/50 text-red-400'
                        : 'bg-[#1A1D23] border-[#2D3139] text-slate-400 hover:text-red-400'
                    }`}
                  >
                    <AlertTriangle className="w-3 h-3" /> Alerte
                  </button>
                  <button
                    onClick={() => setBroadcastType('note')}
                    className={`flex items-center justify-center gap-1.5 py-2 rounded-xl border text-[10px] font-bold transition-all ${
                      broadcastType === 'note'
                        ? 'bg-sky-500/20 border-sky-500/50 text-sky-400'
                        : 'bg-[#1A1D23] border-[#2D3139] text-slate-400 hover:text-sky-400'
                    }`}
                  >
                    <Megaphone className="w-3 h-3" /> Note
                  </button>
                </div>

                {isAdmin && (
                  <select
                    value={broadcastCategoryId}
                    onChange={e => setBroadcastCategoryId(e.target.value)}
                    className="w-full bg-[#1A1D23] border border-[#2D3139] rounded-xl px-3 py-2 text-white text-xs focus:border-indigo-500 focus:outline-none"
                  >
                    <option value="all">🌐 Toutes les catégories</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                )}

                <textarea
                  value={broadcastContent}
                  onChange={e => setBroadcastContent(e.target.value)}
                  placeholder={broadcastType === 'alert' ? 'Message d\'alerte urgente...' : 'Note générale...'}
                  rows={2}
                  className={`w-full bg-[#1A1D23] border rounded-xl px-3 py-2.5 text-white text-xs placeholder-slate-600 focus:outline-none resize-none transition-colors ${
                    broadcastType === 'alert' ? 'border-red-500/30 focus:border-red-500' : 'border-sky-500/30 focus:border-sky-500'
                  }`}
                />

                <button
                  onClick={handleBroadcast}
                  disabled={!broadcastContent.trim() || broadcastSending || broadcastCount === 0}
                  className={`w-full py-2.5 font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 disabled:opacity-40 ${
                    broadcastSent ? 'bg-emerald-600 text-white' : broadcastType === 'alert' ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-sky-600 hover:bg-sky-500 text-white'
                  }`}
                >
                  {broadcastSent ? '✅ Envoyé' : <><Send className="w-3.5 h-3.5" /> Envoyer à {broadcastCount}</>}
                </button>
              </div>
            )}
          </div>

          {/* Search box */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher un contact..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full bg-[#1A1D23] border border-[#2D3139]/80 rounded-xl pl-9 pr-4 py-2 text-xs text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Contacts list */}
          <div className="space-y-2">
            {sortedTeam.length === 0 ? (
              <p className="text-center text-slate-500 text-xs py-8 italic">Aucun contact trouvé</p>
            ) : (
              sortedTeam.filter(s => {
                if (!searchQuery.trim()) return true;
                const q = searchQuery.toLowerCase();
                const cat = categories.find(c => c.id === s.categoryId);
                return s.name.toLowerCase().includes(q) || (cat?.name || '').toLowerCase().includes(q);
              }).map(s => {
                const { lastMsg } = getContactPreview(s.id, s.name);
                const unreadCount = unreadCountForTeam(s.id);
                const cat = categories.find(c => c.id === s.categoryId);

                return (
                  <button
                    key={s.id}
                    onClick={() => isEmailValid(s) ? setSelectedTeamId(s.id) : null}
                    disabled={!isEmailValid(s)}
                    title={!isEmailValid(s) ? 'Email non validé — Contact en lecture seule' : undefined}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left relative ${
                      !isEmailValid(s)
                        ? 'opacity-50 cursor-not-allowed bg-[#0C0E12]/50 border-[#2D3139]/30'
                        : selectedTeamId === s.id
                        ? 'bg-indigo-600/10 border-indigo-500/40 shadow-md shadow-indigo-600/5'
                        : 'bg-[#0C0E12] border-[#2D3139]/50 hover:bg-[#1A1D23]/60 hover:border-[#2D3139]'
                    }`}
                  >
                    {/* Avatar initials with status badge */}
                    <div className="relative flex-shrink-0">
                      {s.photo ? (
                        <img 
                          src={s.photo} 
                          alt={s.name} 
                          className="w-10 h-10 rounded-xl object-cover border border-[#2D3139]" 
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-300 flex items-center justify-center font-bold text-xs">
                          {s.name.substring(0, 2).toUpperCase()}
                        </div>
                      )}
                      {/* Unread Orange Blinker Dot */}
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-500 rounded-full animate-orange-blink border-2 border-[#0A0B0E]" />
                      )}
                    </div>

                    {/* Text fields */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-baseline gap-2">
                        <span className="font-bold text-white text-xs truncate">{s.name}</span>
                        {lastMsg && (
                          <span className="text-[9px] text-slate-500 shrink-0 font-medium font-mono">
                            {new Date(lastMsg.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-[9px] text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.5 rounded font-extrabold shrink-0">
                          {cat?.name || 'Général'}
                        </span>
                        {s.isArtist && (
                          <span className="text-[9px] text-amber-400 bg-amber-400/10 border border-amber-400/30 px-1.5 py-0.5 rounded font-extrabold shrink-0" title="Artiste">
                            👑 Artiste
                          </span>
                        )}
                        {!isEmailValid(s) && (
                          <span className="text-[8px] text-amber-500 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded font-bold shrink-0">
                            Mail non validé
                          </span>
                        )}
                        {lastMsg ? (
                          <p className="text-[10px] text-slate-400 truncate flex-1 font-medium pl-1">
                            {lastMsg.fromTeam ? '📥 ' : '📤 '}
                            {lastMsg.content}
                          </p>
                        ) : (
                          <p className="text-[10px] text-slate-600 italic truncate flex-1 pl-1">Pas de message</p>
                        )}
                        {unreadCount > 0 && (
                          <span className="bg-orange-500 text-white font-extrabold text-[9px] px-1.5 py-0.2 rounded-full flex items-center justify-center scale-95 shrink-0">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* ─── RIGHT COLUMN: ACTIVE CONVERSATION (Desktop View) ─── */}
      <div className="hidden md:flex flex-col flex-1 h-full bg-[#0A0B0E] relative overflow-hidden">
        {!activeTeam ? (
          /* Empty/Blank State */
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-slate-500 space-y-4">
            <div className="w-16 h-16 rounded-full bg-slate-900/60 border border-[#2D3139] flex items-center justify-center shadow-lg shadow-black/10">
              <Inbox className="w-7 h-7 text-slate-600" />
            </div>
            <div className="text-center space-y-1 max-w-sm">
              <p className="font-bold text-white text-sm">Sélectionnez une discussion</p>
              <p className="text-xs text-slate-400 leading-normal">
                Choisissez un membre du team ou un artiste dans la colonne de gauche pour afficher l'historique et lui envoyer un message direct.
              </p>
            </div>
          </div>
        ) : (
          /* Chat Area */
          <>
            {/* Thread Header */}
            <div className="p-5 border-b border-[#2D3139] flex items-center justify-between bg-[#14161B]/60">
              <div className="flex items-center gap-3">
                {activeTeam.photo ? (
                  <img 
                    src={activeTeam.photo} 
                    alt={activeTeam.name} 
                    className="w-10 h-10 rounded-xl object-cover border border-[#2D3139]" 
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-300 flex items-center justify-center font-bold text-sm">
                    {activeTeam.name.substring(0, 2).toUpperCase()}
                  </div>
                )}
                <div>
                  <h3 className="font-bold text-white text-sm leading-snug">{activeTeam.name}</h3>
                  <span className="text-[9px] text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 rounded font-extrabold uppercase tracking-wider inline-block mt-1">
                    {categories.find(c => c.id === activeTeam.categoryId)?.name || 'Général'}
                  </span>
                </div>
              </div>
            </div>

            {/* Chat Messages History */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4 min-h-0 bg-[#0A0B0E]/40" ref={chatScrollRef}>
              {activeMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-slate-500 space-y-3">
                  <div className="w-12 h-12 rounded-full bg-slate-900 border border-[#2D3139] flex items-center justify-center">
                    <Send className="w-4 h-4 text-slate-600" />
                  </div>
                  <p className="text-center text-xs italic">Aucun message échangé. Envoyez un message ci-dessous pour démarrer.</p>
                </div>
              ) : (
                activeMessages.map((m) => {
                  const senderLabel = resolveSender(m, activeTeam.name);
                  const isBroadcast = !m.fromTeam && (m.senderName?.includes('Alerte') || m.senderName?.includes('Note'));
                  const isAlert = isBroadcast && m.senderName?.includes('Alerte');
                  const isNote = isBroadcast && m.senderName?.includes('Note');

                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col max-w-[70%] ${
                        m.fromTeam ? 'mr-auto items-start' : 'ml-auto items-end'
                      }`}
                    >
                      <div
                        className={`rounded-2xl px-4 py-2.5 text-xs leading-relaxed font-medium ${
                          m.fromTeam
                            ? 'bg-[#1C1F26] border border-[#2D3139]/60 text-slate-200 rounded-tl-sm'
                            : isAlert
                            ? 'bg-red-500/10 border border-red-500/30 text-red-300 rounded-tr-sm'
                            : isNote
                            ? 'bg-sky-500/10 border border-sky-500/30 text-sky-300 rounded-tr-sm'
                            : 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/10 rounded-tr-sm'
                        }`}
                      >
                        {m.content}
                        {m.imageUrl && (
                          <img src={m.imageUrl} className="mt-2 max-w-[200px] max-h-[200px] rounded-lg border border-white/10 cursor-pointer hover:opacity-90" alt="img" onClick={() => window.open(m.imageUrl!, '_blank')} />
                        )}
                        {m.voiceUrl && (
                          <audio src={m.voiceUrl} controls className="mt-2 h-8 max-w-[200px]" preload="metadata" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5 px-1 font-mono text-[9px] text-slate-500">
                        <SenderBadge name={senderLabel} isFromTeam={m.fromTeam} />
                        <span>•</span>
                        <span>
                          {new Date(m.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input Bar */}
            <div className="p-5 border-t border-[#2D3139]/50 bg-[#14161B]/60 space-y-2 pb-safe shrink-0">
              {/* Media previews */}
              {attachedImage && (
                <div className="relative inline-block">
                  <img src={attachedImage} className="h-20 rounded-xl border border-[#2D3139]" alt="preview" />
                  <button onClick={() => setAttachedImage(null)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"><X className="w-3 h-3 text-white" /></button>
                </div>
              )}
              {audioBlob && (
                <div className="flex items-center gap-2 bg-[#1A1D23] rounded-xl px-3 py-2">
                  <span className="text-xs text-emerald-400">🎤 Message vocal prêt</span>
                  <audio src={URL.createObjectURL(audioBlob)} controls className="h-7 w-32" />
                  <button onClick={() => setAudioBlob(null)} className="text-red-400"><X className="w-3 h-3" /></button>
                </div>
              )}
              <div className="flex gap-3">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
                <button onClick={() => fileInputRef.current?.click()} className="w-10 h-10 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-xl transition-colors flex items-center justify-center shrink-0" title="Joindre une image">
                  <Image className="w-4 h-4" />
                </button>
                {recording ? (
                  <button onClick={stopRecording} className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors flex items-center justify-center shrink-0 animate-pulse" title="Arrêter l'enregistrement">
                    <Square className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={startRecording} className="w-10 h-10 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-xl transition-colors flex items-center justify-center shrink-0" title="Enregistrer un message vocal">
                    <Mic className="w-4 h-4" />
                  </button>
                )}
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder={`Écrire un message pour ${activeTeam.name}...`}
                  rows={2}
                  className="flex-1 bg-[#1A1D23] border border-[#2D3139] rounded-2xl px-4 py-3 text-white text-xs placeholder-slate-600 focus:border-indigo-500 focus:outline-none resize-none transition-colors"
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!content.trim() && !attachedImage && !audioBlob}
                  className="w-12 h-12 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-2xl transition-all flex items-center justify-center shadow-lg shadow-indigo-600/20 active:scale-95 shrink-0"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ─── MOBILE CONVERSATION DRAWER (Mobile View) ─── */}
      {selectedTeamId && activeTeam && (
        <div className="md:hidden">
          {/* Backdrop overlay */}
          <div 
            className="fixed inset-0 bg-black/60 z-[90] transition-opacity"
            onClick={() => setSelectedTeamId(null)}
          />

          {/* Slide-up bottom drawer sheet */}
          <div className="fixed bottom-0 inset-x-0 h-[85vh] bg-[#14161B] border-t border-[#2D3139] rounded-t-3xl z-[100] flex flex-col transform transition-transform duration-300 translate-y-0 overflow-hidden">
            {/* Drag Handle Indicator */}
            <div className="w-full flex justify-center py-2 shrink-0">
              <div className="w-12 h-1.5 bg-[#2D3139] rounded-full" />
            </div>

            {/* Header */}
            <div className="p-4 border-b border-[#2D3139] flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSelectedTeamId(null)}
                  className="p-1.5 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors"
                  title="Retour"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                
                <div className="flex items-center gap-2.5">
                  {activeTeam.photo ? (
                    <img 
                      src={activeTeam.photo} 
                      alt={activeTeam.name} 
                      className="w-8 h-8 rounded-lg object-cover border border-[#2D3139]" 
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 text-indigo-300 flex items-center justify-center font-bold text-xs">
                      {activeTeam.name.substring(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h4 className="font-bold text-white text-xs truncate max-w-[150px]">{activeTeam.name}</h4>
                    <span className="text-[8px] text-indigo-400 bg-indigo-500/10 border border-indigo-500/20 px-1.5 py-0.2 rounded font-extrabold uppercase tracking-widest mt-0.5 inline-block">
                      {categories.find(c => c.id === activeTeam.categoryId)?.name || 'Général'}
                    </span>
                  </div>
                </div>
              </div>

              <button
                onClick={() => setSelectedTeamId(null)}
                className="p-1.5 hover:bg-[#2D3139] rounded-xl text-slate-400 hover:text-white transition-colors"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-5 space-y-4 min-h-0 bg-[#0A0B0E]/40">
              {activeMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500 space-y-2">
                  <p className="text-center text-xs italic">Aucun message échangé.</p>
                </div>
              ) : (
                activeMessages.map((m) => {
                  const senderLabel = resolveSender(m, activeTeam.name);
                  const isBroadcast = !m.fromTeam && (m.senderName?.includes('Alerte') || m.senderName?.includes('Note'));
                  const isAlert = isBroadcast && m.senderName?.includes('Alerte');
                  const isNote = isBroadcast && m.senderName?.includes('Note');

                  return (
                    <div
                      key={m.id}
                      className={`flex flex-col max-w-[80%] ${
                        m.fromTeam ? 'mr-auto items-start' : 'ml-auto items-end'
                      }`}
                    >
                      <div
                        className={`rounded-2xl px-3.5 py-2 text-xs leading-relaxed font-medium ${
                          m.fromTeam
                            ? 'bg-[#1C1F26] border border-[#2D3139]/60 text-slate-200 rounded-tl-sm'
                            : isAlert
                            ? 'bg-red-500/10 border-red-500/30 text-red-300 rounded-tr-sm'
                            : isNote
                            ? 'bg-sky-500/10 border-sky-500/30 text-sky-300 rounded-tr-sm'
                            : 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/10 rounded-tr-sm'
                        }`}
                      >
                        {m.content}
                        {m.imageUrl && (
                          <img src={m.imageUrl} className="mt-2 max-w-[200px] max-h-[200px] rounded-lg border border-white/10 cursor-pointer hover:opacity-90" alt="img" onClick={() => window.open(m.imageUrl!, '_blank')} />
                        )}
                        {m.voiceUrl && (
                          <audio src={m.voiceUrl} controls className="mt-2 h-8 max-w-[200px]" preload="metadata" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1 px-1 font-mono text-[8px] text-slate-500">
                        <SenderBadge name={senderLabel} isFromTeam={m.fromTeam} />
                        <span>•</span>
                        <span>
                          {new Date(m.timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-[#2D3139]/50 bg-[#14161B] space-y-2 pb-safe shrink-0">
              {/* Media previews */}
              {attachedImage && (
                <div className="relative inline-block">
                  <img src={attachedImage} className="h-16 rounded-xl border border-[#2D3139]" alt="preview" />
                  <button onClick={() => setAttachedImage(null)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"><X className="w-3 h-3 text-white" /></button>
                </div>
              )}
              {audioBlob && (
                <div className="flex items-center gap-2 bg-[#1A1D23] rounded-xl px-3 py-2">
                  <span className="text-xs text-emerald-400">🎤 Message vocal prêt</span>
                  <audio src={URL.createObjectURL(audioBlob)} controls className="h-7 w-24" />
                  <button onClick={() => setAudioBlob(null)} className="text-red-400"><X className="w-3 h-3" /></button>
                </div>
              )}
              <div className="flex gap-2">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
                <button onClick={() => fileInputRef.current?.click()} className="w-10 h-10 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-xl transition-colors flex items-center justify-center shrink-0" title="Joindre une image">
                  <Image className="w-4 h-4" />
                </button>
                {recording ? (
                  <button onClick={stopRecording} className="w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors flex items-center justify-center shrink-0 animate-pulse" title="Arrêter l'enregistrement">
                    <Square className="w-4 h-4" />
                  </button>
                ) : (
                  <button onClick={startRecording} className="w-10 h-10 bg-[#1A1D23] hover:bg-[#2D3139] border border-[#2D3139] text-slate-400 rounded-xl transition-colors flex items-center justify-center shrink-0" title="Enregistrer un message vocal">
                    <Mic className="w-4 h-4" />
                  </button>
                )}
                <textarea
                  value={content}
                  onChange={e => setContent(e.target.value)}
                  placeholder={`Message direct...`}
                  rows={2}
                  className="flex-1 bg-[#1A1D23] border border-[#2D3139] rounded-2xl px-3 py-2 text-white text-xs placeholder-slate-600 focus:border-indigo-500 focus:outline-none resize-none transition-colors"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!content.trim() && !attachedImage && !audioBlob}
                  className="w-10 h-10 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-2xl transition-all flex items-center justify-center shadow-lg active:scale-95 shrink-0"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

